import React, { useState, useEffect, useCallback } from 'react';
import { Container, Table, Badge, Row, Col, Form, FormGroup, FormLabel, Card, InputGroup, Pagination, Tab, Nav, Button, Modal, FormControl } from 'react-bootstrap';
import { Formik, Form as FormikForm, Field, ErrorMessage, FieldArray } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';
import { useApiLoader } from "../../../hooks/useApiLoader.tsx";
import { subscriptionPlansApi, GetSubscriptionsParams, SubscriptionTransaction as ApiSubscriptionTransaction, SubscriptionCounts } from './SubscriptionPlansApi.tsx';

// Import icons
import SearchIcon from "../../../images/search-icon.svg";
import Icon1 from "../../../images/subscription/icon-all-users.png";
import Icon2 from "../../../images/subscription/icon-active-user.png";
import Icon3 from "../../../images/subscription/icon-inactive-users.png";
import ShortMain from "../../../images/short-icon-main.svg";
import ShortUp from "../../../images/short-icon-top.svg";
import ShortDown from "../../../images/short-icon-bottom.svg";
import Amico from "../../../images/amico.png";
import CheckMark from "../../../images/check-mark-icon.svg";
import { HiOutlineDownload } from 'react-icons/hi';
import DateRangeSelector from "../../DateRangeSelector";
import ExportModal from "../../ExportModal";
import { exportSubscriptions, downloadFile, getExportFilename } from '../../../utils/exportUtils';

import "../Support/Support.scss";
import "./SubscriptionPlans.scss";

type SubscriptionTransaction = ApiSubscriptionTransaction;

interface SubscriptionStats {
  totalSubscriptions: number;
  activeSubscriptions: number;
  inactiveSubscriptions: number;
}

interface PaginationInfo {
  currentPage: number;
  totalPages: number;
  totalSubscriptions: number;
  limit: number;
}

interface DateRange {
  startDate: string;
  endDate: string;
}

// Validation schema for plan editing
const planValidationSchema = Yup.object({
  planName: Yup.string()
    .min(2, 'Plan name must be at least 2 characters')
    .max(100, 'Plan name must not exceed 100 characters')
    .required('Plan name is required'),
  priceMonthly: Yup.number()
    .min(0.01, 'Monthly price must be at least $0.01')
    .max(9999.99, 'Monthly price must not exceed $9999.99')
    .required('Monthly price is required'),
  priceYearly: Yup.number()
    .min(0.01, 'Yearly price must be at least $0.01')
    .max(99999.99, 'Yearly price must not exceed $99999.99')
    .required('Yearly price is required'),
  description: Yup.string()
    .min(10, 'Description must be at least 10 characters')
    .max(500, 'Description must not exceed 500 characters')
    .required('Description is required'),
  features: Yup.array()
    .of(Yup.string().trim().min(1, 'Feature cannot be empty').required('Feature is required'))
    .min(1, 'At least one feature is required')
    .max(20, 'Maximum 20 features allowed')
    .test('no-empty-features', 'All features must have content', function(value) {
      if (!value) return false;
      return value.every(feature => feature && feature.trim().length > 0);
    })
});

const SubscriptionPlans: React.FC = () => {
  const navigate = useNavigate();
  const { withLoader } = useApiLoader();
  const [activeTab, setActiveTab] = useState<string>('all-transactions');
  const [managePlanTab, setManagePlanTab] = useState<string>('monthly');

  // State for All Transactions Tab
  const [subscriptions, setSubscriptions] = useState<SubscriptionTransaction[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [subscriptionStats, setSubscriptionStats] = useState<SubscriptionStats>({
    totalSubscriptions: 0,
    activeSubscriptions: 0,
    inactiveSubscriptions: 0
  });
  const [pagination, setPagination] = useState<PaginationInfo>({
    currentPage: 1,
    totalPages: 1,
    totalSubscriptions: 0,
    limit: 10
  });

  // Filter states
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [sortField, setSortField] = useState<string>('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [selectedCardFilter, setSelectedCardFilter] = useState<string>('');
  const [showExportModal, setShowExportModal] = useState(false);
  const [dateRange, setDateRange] = useState<DateRange>({
    startDate: '',
    endDate: ''
  });

  // Subscription plan data for Manage Plans tab (will be loaded from API)
  const [subscriptionPlan, setSubscriptionPlan] = useState<any>({
    _id: '',
    name: "Ultimate VIP",
    description: "All-in-one package",
    priceMonthly: 9.99,
    priceYearly: 99.00,
    planType: "Premium",
    features: [
      "Priority Gift Processing",
      "Premium Gift Selection",
      "Exclusive Gift Wrapping",
      "Customizable Gift Options",
      "Early Access to New Features",
      "Gift Personalization Messages",
      "Luxury Gift Packaging",
      "Anonymous & Personalized Giving"
    ]
  });
  const [planLoading, setPlanLoading] = useState(false);

  // Edit Modal State
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [selectedPriceType, setSelectedPriceType] = useState<'monthly' | 'yearly'>('monthly');
  const [editFormData, setEditFormData] = useState({
    planName: subscriptionPlan.name,
    planType: subscriptionPlan.planType,
    priceMonthly: subscriptionPlan.priceMonthly.toString(),
    priceYearly: subscriptionPlan.priceYearly.toString(),
    description: subscriptionPlan.description,
    features: [...subscriptionPlan.features],
    isEnabled: true
  });

  // Fetch subscriptions from API
  const fetchSubscriptions = useCallback(async (params: GetSubscriptionsParams = {}) => {
    try {
      setLoading(true);

      const [subscriptionsResponse, countsResponse] = await Promise.all([
        withLoader(
          () => subscriptionPlansApi.getAllSubscriptions(params),
          'Loading subscriptions...'
        ),
        withLoader(
          () => subscriptionPlansApi.getSubscriptionCounts(),
          'Loading statistics...'
        )
      ]);

      if (subscriptionsResponse.status === 'Success' && subscriptionsResponse.data) {
        setSubscriptions(subscriptionsResponse.data || []);
        setPagination(prev => ({
          ...(subscriptionsResponse.pagination || {
            currentPage: 1,
            totalPages: 1,
            totalSubscriptions: 0,
            limit: 10
          }),
          limit: params.limit || prev.limit
        }));
      } else {
        toast.error(subscriptionsResponse.message || 'Failed to fetch subscriptions');
        setSubscriptions([]);
        setPagination({
          currentPage: 1,
          totalPages: 1,
          totalSubscriptions: 0,
          limit: 10
        });
      }

      if (countsResponse.status === 'Success' && countsResponse.subscriptionCounts) {
        setSubscriptionStats(countsResponse.subscriptionCounts);
      } else {
        setSubscriptionStats({
          totalSubscriptions: 0,
          activeSubscriptions: 0,
          inactiveSubscriptions: 0
        });
      }

    } catch (error: any) {
      console.error('Error fetching subscriptions:', error);
      toast.error('Failed to fetch subscriptions');
      setSubscriptions([]);
      setPagination({
        currentPage: 1,
        totalPages: 1,
        totalSubscriptions: 0,
        limit: 10
      });
      setSubscriptionStats({
        totalSubscriptions: 0,
        activeSubscriptions: 0,
        inactiveSubscriptions: 0
      });
    } finally {
      setLoading(false);
    }
  }, [withLoader]);

  const applyFilters = useCallback(() => {
    const params: GetSubscriptionsParams = {
      page: 1,
      limit: pagination.limit,
      sortBy: sortField,
      sortOrder: sortOrder,
    };

    if (searchTerm.trim()) {
      params.search = searchTerm.trim();
    }

    if (statusFilter) {
      params.status = statusFilter;
    }

    if (dateRange.startDate) {
      params.startDate = dateRange.startDate;
    }

    if (dateRange.endDate) {
      params.endDate = dateRange.endDate;
    }

    fetchSubscriptions(params);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, statusFilter, sortField, sortOrder, pagination.limit, dateRange]);

  const handlePageChange = (page: number) => {
    const params: GetSubscriptionsParams = {
      page,
      limit: pagination.limit,
      sortBy: sortField,
      sortOrder: sortOrder,
    };

    if (searchTerm.trim()) params.search = searchTerm.trim();
    if (statusFilter) params.status = statusFilter;
    if (dateRange.startDate) params.startDate = dateRange.startDate;
    if (dateRange.endDate) params.endDate = dateRange.endDate;

    fetchSubscriptions(params);
  };

  const handleSort = (field: string) => {
    const newOrder = sortField === field && sortOrder === 'desc' ? 'asc' : 'desc';
    setSortField(field);
    setSortOrder(newOrder);

    const params: GetSubscriptionsParams = {
      page: pagination.currentPage || 1,
      limit: pagination.limit,
      sortBy: field,
      sortOrder: newOrder,
    };

    if (searchTerm.trim()) params.search = searchTerm.trim();
    if (statusFilter) params.status = statusFilter;
    if (dateRange.startDate) params.startDate = dateRange.startDate;
    if (dateRange.endDate) params.endDate = dateRange.endDate;

    fetchSubscriptions(params);
  };

  const handleCardClick = (cardType: string) => {
    const cardValues = {
      'all': subscriptionStats.totalSubscriptions,
      'active': subscriptionStats.activeSubscriptions,
      'inactive': subscriptionStats.inactiveSubscriptions
    };

    if (cardValues[cardType as keyof typeof cardValues] === 0) {
      return;
    }

    let statusFilterValue = '';

    switch (cardType) {
      case 'active':
        statusFilterValue = 'active';
        break;
      case 'inactive':
        statusFilterValue = 'canceled';
        break;
      case 'all':
      default:
        statusFilterValue = '';
        break;
    }

    setSelectedCardFilter(statusFilterValue);
    setStatusFilter(statusFilterValue);

    const params: GetSubscriptionsParams = {
      page: 1,
      limit: pagination.limit,
      sortBy: sortField,
      sortOrder: sortOrder,
    };

    if (searchTerm.trim()) params.search = searchTerm.trim();
    if (statusFilterValue) params.status = statusFilterValue;
    if (dateRange.startDate) params.startDate = dateRange.startDate;
    if (dateRange.endDate) params.endDate = dateRange.endDate;

    fetchSubscriptions(params);
  };

  const handleRowClick = (subscription: SubscriptionTransaction) => {
    const subscriptionId = subscription.transactionId || subscription._id || subscription.id;
    navigate(`/subscriptions/${subscriptionId}`);
  };

  const getSortIcon = (field: string) => {
    if (sortField !== field) return ShortMain;
    return sortOrder === 'asc' ? ShortUp : ShortDown;
  };

  const getStatusBadge = (status: string) => {
    let badgeClass = '';
    let displayText = status;

    switch (status?.toLowerCase()) {
      case 'active':
        badgeClass = 'status-active badge rounded-pill bg-primary';
        displayText = 'Active';
        break;
      case 'inactive':
      case 'cancelled':
      case 'canceled':
      case 'incomplete':
        badgeClass = 'badge rounded-pill bg-danger';
        displayText = 'Inactive';
        break;
      case 'pending':
        badgeClass = 'warning';
        displayText = 'Pending';
        break;
      case 'expired':
        badgeClass = 'secondary';
        displayText = 'Expired';
        break;
      default:
        badgeClass = 'secondary';
        displayText = status || 'Unknown';
    }
    return <Badge className={badgeClass}>{displayText}</Badge>;
  };

  const getPlanTypeBadge = (planType: string) => {
    return <Badge bg="blu-badge">{planType}</Badge>;
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric'
      });
    } catch {
      return '';
    }
  };

  // Format date for API (YYYY-MM-DD format)
  const formatDateForAPI = (dateString: string): string => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      return date.toISOString().split('T')[0];
    } catch (error) {
      return '';
    }
  };

  // Edit Modal Functions for Manage Plans Tab
  const handleEditClick = () => {
    // Update form data based on subscription plan state
    setEditFormData({
      planName: subscriptionPlan.name,
      planType: subscriptionPlan.planType,
      priceMonthly: subscriptionPlan.priceMonthly.toString(),
      priceYearly: subscriptionPlan.priceYearly.toString(),
      description: subscriptionPlan.description,
      features: [...subscriptionPlan.features],
      isEnabled: true
    });
    setSelectedPriceType('monthly'); // Reset to monthly view
    setShowEditModal(true);
  };

  const handleCloseModal = () => {
    Swal.fire({
      title: 'Are you sure?',
      text: "Any unsaved changes will be lost!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3957A0',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, close it!'
    }).then((result) => {
      if (result.isConfirmed) {
        setShowEditModal(false);
        // Reset form data
        setEditFormData({
          planName: subscriptionPlan.name,
          planType: subscriptionPlan.planType,
          priceMonthly: subscriptionPlan.priceMonthly.toString(),
          priceYearly: subscriptionPlan.priceYearly.toString(),
          description: subscriptionPlan.description,
          features: [...subscriptionPlan.features],
          isEnabled: true
        });
      }
    });
  };

  // Helper function to check if form has changes
  const hasFormChanges = (values: any) => {
    const originalData = {
      planName: subscriptionPlan.name,
      priceMonthly: subscriptionPlan.priceMonthly.toString(),
      priceYearly: subscriptionPlan.priceYearly.toString(),
      description: subscriptionPlan.description,
      features: subscriptionPlan.features,
      isEnabled: true
    };

    // Check if basic fields changed
    if (
      values.planName !== originalData.planName ||
      values.priceMonthly !== originalData.priceMonthly ||
      values.priceYearly !== originalData.priceYearly ||
      values.description !== originalData.description ||
      values.isEnabled !== originalData.isEnabled
    ) {
      return true;
    }

    // Check if features changed
    if (values.features.length !== originalData.features.length) {
      return true;
    }

    for (let i = 0; i < values.features.length; i++) {
      if (values.features[i].trim() !== originalData.features[i]) {
        return true;
      }
    }

    return false;
  };

  // Fetch subscription plan
  const fetchSubscriptionPlan = useCallback(async () => {
    try {
      setPlanLoading(true);

      // First get all plans to find the plan ID
      const plansResponse = await withLoader(
        () => subscriptionPlansApi.getSubscriptionPlans(),
        'Loading subscription plans...'
      );

      if (plansResponse.status === 'Success' && plansResponse.data && plansResponse.data.length > 0) {
        // Use the first plan's data (assuming there's one main plan)
        const plan = plansResponse.data[0];
        setSubscriptionPlan(plan);
      } else {
        toast.error(plansResponse.message || 'No subscription plans found');
      }
    } catch (error: any) {
      console.error('Error fetching subscription plan:', error);
      toast.error('Failed to fetch subscription plan');
    } finally {
      setPlanLoading(false);
    }
  }, [withLoader]);

  const handleUpdatePlan = async (values: any) => {
    // Check for empty features
    const hasEmptyFeatures = values.features.some((feature: string) => !feature || feature.trim().length === 0);
    if (hasEmptyFeatures) {
      toast.error('Please fill in all feature fields or remove empty ones');
      return;
    }

    // Show confirmation dialog
    const result = await Swal.fire({
      title: 'Update Subscription Plan?',
      text: "Are you sure you want to update this subscription plan?",
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#3957A0',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, update it!',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      try {
        const planId = subscriptionPlan._id || subscriptionPlan.id;

        if (!planId) {
          toast.error('Plan ID not found. Please refresh and try again.');
          return;
        }

        const updateParams = {
          name: values.planName,
          description: values.description,
          priceMonthly: parseFloat(values.priceMonthly),
          priceYearly: parseFloat(values.priceYearly),
          features: values.features.filter((f: string) => f.trim())
        };

        const response = await withLoader(
          () => subscriptionPlansApi.updatePlan(planId, updateParams),
          'Updating subscription plan...'
        );

        if (response.status === 'Success') {
          toast.success('Subscription plan updated successfully!');
          setShowEditModal(false);

          // Refresh plan data
          await fetchSubscriptionPlan();

          // Show success confirmation
          Swal.fire({
            title: 'Updated!',
            text: 'Your subscription plan has been updated successfully.',
            icon: 'success',
            confirmButtonColor: '#3957A0'
          });
        } else {
          toast.error(response.message || 'Failed to update subscription plan');
        }

      } catch (error: any) {
        console.error('Error updating plan:', error);
        toast.error('Failed to update subscription plan. Please try again.');

        Swal.fire({
          title: 'Error!',
          text: 'Failed to update the subscription plan. Please try again.',
          icon: 'error',
          confirmButtonColor: '#3957A0'
        });
      }
    }
  };

  useEffect(() => {
    if (activeTab === 'all-transactions') {
      fetchSubscriptions();
    } else if (activeTab === 'manage-plans') {
      fetchSubscriptionPlan();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Auto-search when searchTerm changes (debounced)
  useEffect(() => {
    if (activeTab === 'all-transactions') {
      const timer = setTimeout(() => {
        const params: GetSubscriptionsParams = {
          page: 1,
          limit: pagination.limit,
          sortBy: sortField,
          sortOrder: sortOrder,
        };

        if (searchTerm.trim()) {
          params.search = searchTerm.trim();
        }

        if (statusFilter) {
          params.status = statusFilter;
        }

        if (dateRange.startDate) {
          params.startDate = dateRange.startDate;
        }

        if (dateRange.endDate) {
          params.endDate = dateRange.endDate;
        }

        fetchSubscriptions(params);
      }, 500);

      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  // Re-fetch when page size (limit) changes
  useEffect(() => {
    if (activeTab === 'all-transactions') {
      const params: GetSubscriptionsParams = {
        page: 1,
        limit: pagination.limit,
        sortBy: sortField,
        sortOrder: sortOrder,
      };

      if (searchTerm.trim()) params.search = searchTerm.trim();
      if (statusFilter) params.status = statusFilter;
      if (dateRange.startDate) params.startDate = dateRange.startDate;
      if (dateRange.endDate) params.endDate = dateRange.endDate;

      fetchSubscriptions(params);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.limit]);

  // Handle date range change
  const handleDateRangeChange = useCallback((dateRangeString: string, isCustom?: boolean) => {
    // Parse the date range string "MM/DD/YYYY - MM/DD/YYYY"
    if (!dateRangeString || dateRangeString.trim() === '') {
      // Clear the filter
      setDateRange({ startDate: '', endDate: '' });

      const params: GetSubscriptionsParams = {
        page: 1,
        limit: pagination.limit,
        sortBy: sortField,
        sortOrder: sortOrder,
      };

      if (searchTerm.trim()) params.search = searchTerm.trim();
      if (statusFilter) params.status = statusFilter;

      fetchSubscriptions(params);
      return;
    }

    // Split the date range string
    const dates = dateRangeString.split(' - ');
    if (dates.length === 2) {
      const startDateStr = dates[0].trim();
      const endDateStr = dates[1].trim();

      // Convert MM/DD/YYYY to YYYY-MM-DD without timezone conversion
      const parseDate = (dateStr: string): string => {
        const [month, day, year] = dateStr.split('/');
        return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      };

      const formattedStartDate = parseDate(startDateStr);
      const formattedEndDate = parseDate(endDateStr);

      // Validate dates
      if (formattedStartDate > formattedEndDate) {
        toast.error('Start date cannot be after end date');
        return;
      }

      setDateRange({ startDate: formattedStartDate, endDate: formattedEndDate });

      const params: GetSubscriptionsParams = {
        page: 1,
        limit: pagination.limit,
        sortBy: sortField,
        sortOrder: sortOrder,
        startDate: formattedStartDate,
        endDate: formattedEndDate,
      };

      if (searchTerm.trim()) params.search = searchTerm.trim();
      if (statusFilter) params.status = statusFilter;

      fetchSubscriptions(params);
    }
  }, [searchTerm, statusFilter, sortField, sortOrder, pagination.limit, fetchSubscriptions]);

  // Handle export
  const handleExport = () => {
    setShowExportModal(true);
  };

  const handleExportExcel = async () => {
    try {
      toast.info('Preparing Excel export...');
      const blob = await exportSubscriptions('excel', dateRange.startDate, dateRange.endDate);
      const filename = getExportFilename('Subscriptions', 'excel', dateRange.startDate, dateRange.endDate);
      downloadFile(blob, filename);
      toast.success('Excel file downloaded successfully!');
    } catch (error: any) {
      console.error('Export Excel error:', error);
      if (error.response?.status === 404) {
        toast.error('No subscriptions found in the selected date range.');
      } else if (error.response?.status === 400) {
        toast.error(error.response?.data?.message || 'Invalid export parameters.');
      } else {
        toast.error('Failed to export Excel file. Please try again.');
      }
    }
  };

  const handleExportPdf = async () => {
    try {
      toast.info('Preparing PDF export...');
      const blob = await exportSubscriptions('pdf', dateRange.startDate, dateRange.endDate);
      const filename = getExportFilename('Subscriptions', 'pdf', dateRange.startDate, dateRange.endDate);
      downloadFile(blob, filename);
      toast.success('PDF file downloaded successfully!');
    } catch (error: any) {
      console.error('Export PDF error:', error);
      if (error.response?.status === 404) {
        toast.error('No subscriptions found in the selected date range.');
      } else if (error.response?.status === 400) {
        toast.error(error.response?.data?.message || 'Invalid export parameters.');
      } else {
        toast.error('Failed to export PDF file. Please try again.');
      }
    }
  };

  const renderPaginationItems = () => {
    const items = [];
    const { currentPage, totalPages } = pagination;
    const maxVisiblePages = 5;

    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let page = startPage; page <= endPage; page++) {
      items.push(
        <Pagination.Item
          key={page}
          active={page === currentPage}
          onClick={() => handlePageChange(page)}
        >
          {page}
        </Pagination.Item>
      );
    }

    return items;
  };

  return (
    <section className='main-body'>
      <Container fluid className="p-0">
        <Card.Body>
          <Tab.Container activeKey={activeTab} onSelect={(k) => k && setActiveTab(k)}>
            {/* Tab Navigation */}
            <Nav variant="pills" className="mb-0">
              <Nav.Item>
                <Nav.Link
                  active={activeTab === 'all-transactions'}
                  onClick={() => setActiveTab('all-transactions')}
                >
                  All Transactions
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link
                  active={activeTab === 'manage-plans'}
                  onClick={() => setActiveTab('manage-plans')}
                >
                  Manage Subscription Plan
                </Nav.Link>
              </Nav.Item>
            </Nav>

            {/* Tab Content */}
            <div className="tab-content">
              {/* All Transactions Tab */}
              <div className={`tab-pane ${activeTab === 'all-transactions' ? 'active show' : ''}`}>
              {/* Statistics Cards */}
              <div className="icon-grid-sec">
                <ul className='icon-grid mb-4'>
                  <div className='row-3 m-0'>
                    <li
                      className={`icon-block ${selectedCardFilter === '' ? 'active-card' : ''} ${subscriptionStats.totalSubscriptions === 0 ? 'non-clickable' : ''}`}
                      onClick={() => subscriptionStats.totalSubscriptions > 0 && handleCardClick('all')}
                      style={{ cursor: subscriptionStats.totalSubscriptions > 0 ? 'pointer' : 'not-allowed', opacity: subscriptionStats.totalSubscriptions > 0 ? 1 : 0.6 }}
                    >
                      <div className="icon-block-in d-flex justify-content-between align-items-center">
                        <figure><img src={Icon1} alt="" /></figure>
                        <div className="icon-grid-cont">
                          <h2 className="blue-text">
                            {subscriptionStats.totalSubscriptions}
                          </h2>
                          <p>Total Subscriptions</p>
                        </div>
                      </div>
                    </li>
                    <li
                      className={`icon-block ${selectedCardFilter === 'active' ? 'active-card' : ''} ${subscriptionStats.activeSubscriptions === 0 ? 'non-clickable' : ''}`}
                      onClick={() => subscriptionStats.activeSubscriptions > 0 && handleCardClick('active')}
                      style={{ cursor: subscriptionStats.activeSubscriptions > 0 ? 'pointer' : 'not-allowed', opacity: subscriptionStats.activeSubscriptions > 0 ? 1 : 0.6 }}
                    >
                      <div className="icon-block-in d-flex justify-content-between align-items-center">
                        <figure><img src={Icon2} alt="" /></figure>
                        <div className="icon-grid-cont">
                          <h2 className="green-text">
                            {subscriptionStats.activeSubscriptions}
                          </h2>
                          <p>Active</p>
                        </div>
                      </div>
                    </li>
                    <li
                      className={`icon-block ${selectedCardFilter === 'canceled' ? 'active-card' : ''} ${subscriptionStats.inactiveSubscriptions === 0 ? 'non-clickable' : ''}`}
                      onClick={() => subscriptionStats.inactiveSubscriptions > 0 && handleCardClick('inactive')}
                      style={{ cursor: subscriptionStats.inactiveSubscriptions > 0 ? 'pointer' : 'not-allowed', opacity: subscriptionStats.inactiveSubscriptions > 0 ? 1 : 0.6 }}
                    >
                      <div className="icon-block-in d-flex justify-content-between align-items-center">
                        <figure><img src={Icon3} alt="" /></figure>
                        <div className="icon-grid-cont">
                          <h2 className="orange-text">
                            {subscriptionStats.inactiveSubscriptions}
                          </h2>
                          <p>Inactive</p>
                        </div>
                      </div>
                    </li>
                  </div>
                  <div className="clearfix"></div>
                </ul>
              </div>

              {/* Filter Section */}
              <div className="management-sec">
                <div className="management-sec-in">
                  <div className='main-table'>
                    <div className="tab-content tab-body">
                      <div className="referral-discount mb-3">
                        {/* Filter and Search Row */}
                        <div className="mb-3 main-table-filter">
                          <div className="row g-2 align-items-stretch">
                            {/* Search Field */}
                            <div className='col-lg-3 col-md-6 col-12'>
                              <div className='main-table-search h-100'>
                                <FormGroup className='form-group search-box mb-0 h-100'>
                                  <InputGroup className="h-100">
                                    <Form.Control
                                      type="text"
                                      placeholder="Search subscriptions..."
                                      value={searchTerm}
                                      onChange={(e) => setSearchTerm(e.target.value)}
                                      className="h-100"
                                    />
                                    <InputGroup.Text>
                                      <img src={SearchIcon} alt='' />
                                    </InputGroup.Text>
                                  </InputGroup>
                                </FormGroup>
                              </div>
                            </div>

                            {/* Filter Dropdown */}
                            <div className='col-lg-2 col-md-6 col-12'>
                              <div className='select-field h-100'>
                                <FormGroup className='form-group mb-0 h-100'>
                                  <Form.Select
                                    value={statusFilter}
                                    onChange={(e) => {
                                      const newValue = e.target.value;
                                      setStatusFilter(newValue);
                                      setSelectedCardFilter(newValue);

                                      // Fetch with new filter value directly
                                      const params: GetSubscriptionsParams = {
                                        page: 1,
                                        limit: pagination.limit,
                                        sortBy: sortField,
                                        sortOrder: sortOrder,
                                      };

                                      if (searchTerm.trim()) params.search = searchTerm.trim();
                                      if (newValue) params.status = newValue;
                                      if (dateRange.startDate) params.startDate = dateRange.startDate;
                                      if (dateRange.endDate) params.endDate = dateRange.endDate;

                                      fetchSubscriptions(params);
                                    }}
                                    className="h-100"
                                  >
                                    <option value="">Filter by Status</option>
                                    <option value="active">Active</option>
                                    <option value="canceled">Canceled</option>
                                  </Form.Select>
                                </FormGroup>
                              </div>
                            </div>

                            {/* Spacer to push date range and export to the right */}
                            <div className='col-lg-3 d-none d-lg-block'></div>

                            {/* Date Range Picker */}
                            <div className='col-lg-3 col-md-9 col-12'>
                              <div className='calender-block h-100'>
                                <DateRangeSelector
                                  onDateRangeChange={handleDateRangeChange}
                                />
                              </div>
                            </div>

                            {/* Export Button */}
                            <div className='col-lg-1 col-md-3 col-12'>
                              <Button
                                variant="primary"
                                onClick={handleExport}
                                className="d-flex align-items-center justify-content-center gap-2 h-100 w-100"
                              >
                                <HiOutlineDownload size={18} />
                                <span>Export</span>
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Subscriptions Table */}
                      <div className="table-responsive mt-3">
                  <Table hover className="mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>User</th>
                        <th>Email</th>
                        <th>Trans ID</th>
                        <th>Start Date</th>
                        <th>End Date</th>
                        <th>Plan Type</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan={7} className="text-center py-4">
                            <div className="d-flex justify-content-center align-items-center">
                              <div className="spinner-border text-primary me-2" role="status">
                                <span className="visually-hidden">Loading...</span>
                              </div>
                              Loading subscriptions...
                            </div>
                          </td>
                        </tr>
                      ) : subscriptions.length === 0 ? (
                        <tr>
                          <td colSpan={7} className="text-center py-4">
                            <div className="text-muted">
                              {searchTerm || statusFilter
                                ? 'No subscriptions found matching your filters'
                                : 'No subscriptions available'
                              }
                            </div>
                          </td>
                        </tr>
                      ) : (
                        subscriptions.map((subscription) => (
                          <tr
                            key={subscription._id || subscription.id}
                            onClick={() => handleRowClick(subscription)}
                            style={{
                              cursor: 'pointer',
                              transition: 'background-color 0.2s ease'
                            }}
                            className="clickable-row"
                          >
                            <td>{subscription.customerName || subscription.userName || 'N/A'}</td>
                            <td>{subscription.customerEmail || subscription.email || 'N/A'}</td>
                            <td>{subscription.transactionId8Digit || subscription.transactionId}</td>
                            <td>{formatDate(subscription.startDate)}</td>
                            <td>{formatDate(subscription.endDate)}</td>
                            <td className="info-badge-area">
                              <p className="d-flex align-items-center gap-1 mb-0">
                                {(() => {
                                  const type = (subscription.billingCycle || subscription.subscriptionType || '').toLowerCase();
                                  if (!type || type === 'n/a' || type === 'null' || type === 'undefined') {
                                    return getPlanTypeBadge('N/A');
                                  }
                                  return getPlanTypeBadge(type === 'monthly' ? 'Monthly' : 'Yearly');
                                })()}
                              </p>
                            </td>
                            <td className="info-badge-area">
                              <p className="d-flex align-items-center gap-1 mb-0">
                                {getStatusBadge(subscription.status)}
                              </p>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                </div>

                {/* Pagination */}
                {pagination.totalPages > 1 && (
                  <div className="orders-pagination-wrapper">
                    <div className="pagination-info-section">
                      <div className="pagination-showing">
                        Showing {((pagination.currentPage - 1) * pagination.limit) + 1} to{' '}
                        {Math.min(pagination.currentPage * pagination.limit, pagination.totalSubscriptions)} of{' '}
                        {pagination.totalSubscriptions} entries
                        {(dateRange.startDate || dateRange.endDate) && (
                          <span className="ms-2">(filtered by date range)</span>
                        )}
                      </div>
                    </div>

                    <div className="pagination-controls-section">
                      <div className="pagination-per-page">
                        <span className="pagination-label">Show</span>
                        <select
                          className="form-select form-select-sm pagination-select"
                          value={pagination.limit}
                          onChange={(e) => setPagination(prev => ({ ...prev, limit: Number(e.target.value), currentPage: 1 }))}
                        >
                          <option value={10}>10</option>
                          <option value={25}>25</option>
                          <option value={50}>50</option>
                        </select>
                      </div>

                      <Pagination className="mb-0 orders-pagination">
                        <Pagination.Prev
                          disabled={pagination.currentPage === 1}
                          onClick={() => handlePageChange(pagination.currentPage - 1)}
                        />
                        {renderPaginationItems()}
                        <Pagination.Next
                          disabled={pagination.currentPage === pagination.totalPages}
                          onClick={() => handlePageChange(pagination.currentPage + 1)}
                        />
                      </Pagination>
                    </div>
                  </div>
                )}
                    </div>
                  </div>
                </div>
              </div>
              </div>

              {/* Manage Subscription Plans Tab */}
              <div className={`tab-pane ${activeTab === 'manage-plans' ? 'active show' : ''}`}>
              <div className="subscription-manage-section">
                <div className="subscription-sec-in">
                  <Row className="justify-content-center">
                    {/* Left Column - Plan Details */}
                    <Col lg={12} md={12} className="subscription-left-block">
                      <Card className="h-100 subscription-plan-card">
                        <Card.Body className="p-4">
                          <div className="plan-header d-flex justify-content-between align-items-center">
                            <h3 className="text-primary fw-bold mb-4">
                              Current Plans
                              <Button  size="sm" className='ms-3' disabled style={{ cursor: 'not-allowed', backgroundColor:"#D7A360" }}>
                              Published
                            </Button>
                            </h3>
                            
                          </div>

                          {/* Custom Tab Navigation */}
                          <Nav variant="pills" className="mb-0">
                            <Nav.Item>
                              <Nav.Link
                                active={managePlanTab === 'monthly'}
                                onClick={() => setManagePlanTab('monthly')}
                              >
                                Monthly
                              </Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                              <Nav.Link
                                active={managePlanTab === 'yearly'}
                                onClick={() => setManagePlanTab('yearly')}
                              >
                                Yearly
                              </Nav.Link>
                            </Nav.Item>
                          </Nav>

                          {/* Tab Content */}
                          <div className="tab-content">
                            {/* Monthly Tab Pane */}
                            <div className={`tab-pane ${managePlanTab === 'monthly' ? 'active show' : ''}`}>
                              <Card
                                className="mb-4 border-0 box-card-block plan-price-card"
                              >
                                <Card.Body className="p-4 text-left position-relative">
                                  <div className='price-display'>
                                    <div className='price-info'>
                                      <h1 className="display-2 fw-bold mb-0">
                                        ${subscriptionPlan.priceMonthly}
                                      </h1>
                                      <p className="mb-0 fs-5 opacity-90">
                                        /month
                                      </p>
                                      <p className="mb-0 fs-5 opacity-90">
                                        {subscriptionPlan.description}
                                      </p>
                                    </div>
                                    <div className="plan-image">
                                      <img src={Amico} alt="" width={150}/>
                                    </div>
                                  </div>
                                </Card.Body>
                              </Card>

                              <div className="features-section">
                                <h5 className="mb-3 fw-bold" style={{fontSize:'18px', color:"#677289"}}>What's included</h5>
                                <ul className="features-list">
                                  {subscriptionPlan.features.map((feature: string, index: number) => (
                                    <li key={index} className="d-flex align-items-center mb-3">
                                      <img src={CheckMark} alt="" style={{ width: "20px", height: "20px", marginRight: "15px" }} />
                                      <span className="fw-bold" style={{color:"#3957A0", fontSize:"16px"}}>
                                        {feature}
                                      </span>
                                    </li>
                                  ))}
                                </ul>
                              </div>

                              <div className="mt-4">
                                <Button
                                  variant="primary"
                                  className="px-4"
                                  style={{ background: "#3957A0", borderColor: "#3957A0" }}
                                  onClick={handleEditClick}
                                >
                                  Edit
                                </Button>
                              </div>
                            </div>

                            {/* Yearly Tab Pane */}
                            <div className={`tab-pane ${managePlanTab === 'yearly' ? 'active show' : ''}`}>
                              <Card
                                className="mb-4 border-0 box-card-block plan-price-card"
                              >
                                <Card.Body className="p-4 text-left position-relative">
                                  <div className='price-display'>
                                    <div className='price-info'>
                                      <h1 className="display-2 fw-bold mb-0">
                                        ${subscriptionPlan.priceYearly}
                                      </h1>
                                      <p className="mb-0 fs-5 opacity-90">
                                        /year
                                      </p>
                                      <p className="mb-0 fs-5 opacity-90">
                                        {subscriptionPlan.description}
                                      </p>
                                    </div>
                                    <div className="plan-image">
                                      <img src={Amico} alt="" width={150}/>
                                    </div>
                                  </div>
                                </Card.Body>
                              </Card>

                              <div className="features-section">
                                <h5 className="mb-3 fw-bold" style={{fontSize:'18px', color:"#677289"}}>What's included</h5>
                                <ul className="features-list">
                                  {subscriptionPlan.features.map((feature: string, index: number) => (
                                    <li key={index} className="d-flex align-items-center mb-3">
                                      <img src={CheckMark} alt="" style={{ width: "20px", height: "20px", marginRight: "15px" }} />
                                      <span className="fw-bold" style={{color:"#3957A0", fontSize:"16px"}}>
                                        {feature}
                                      </span>
                                    </li>
                                  ))}
                                </ul>
                              </div>

                              <div className="mt-4">
                                <Button
                                  variant="primary"
                                  className="px-4"
                                  style={{ background: "#3957A0", borderColor: "#3957A0" }}
                                  onClick={handleEditClick}
                                >
                                  Edit
                                </Button>
                              </div>
                            </div>
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>
                </div>
              </div>
              </div>
            </div>
          </Tab.Container>
        </Card.Body>
      </Container>

      {/* Edit Subscription Plan Modal with Formik */}
      <Modal
        show={showEditModal}
        onHide={handleCloseModal}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Edit Subscription Plans</Modal.Title>
        </Modal.Header>

        <Formik
          initialValues={{
            planName: editFormData.planName,
            planType: editFormData.planType,
            priceMonthly: editFormData.priceMonthly,
            priceYearly: editFormData.priceYearly,
            description: editFormData.description,
            features: editFormData.features,
            isEnabled: editFormData.isEnabled
          }}
          validationSchema={planValidationSchema}
          onSubmit={handleUpdatePlan}
          enableReinitialize
        >
          {({ values, errors, touched, isValid, isSubmitting, setFieldValue }) => {
            const hasChanges = hasFormChanges(values);

            return (
              <FormikForm>
                <Modal.Body>
                <Row>
                  <Col md={6}>
                    <FormGroup className="mb-3">
                      <FormLabel>Plan Name *</FormLabel>
                      <Field
                        as={FormControl}
                        type="text"
                        name="planName"
                        placeholder="Ultimate VIP"
                        isInvalid={errors.planName && touched.planName}
                      />
                      <ErrorMessage name="planName" component="div" className="text-danger small mt-1" />
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup className="mb-3">
                      <FormLabel>Select Price Type *</FormLabel>
                      <Form.Select
                        value={selectedPriceType}
                        onChange={(e) => setSelectedPriceType(e.target.value as 'monthly' | 'yearly')}
                        style={{ padding: '0.375rem 0.75rem' }}
                      >
                        <option value="monthly">Monthly</option>
                        <option value="yearly">Yearly</option>
                      </Form.Select>
                      <Form.Text className="text-muted">Select which price to edit</Form.Text>
                    </FormGroup>
                  </Col>
                </Row>

                <Row>
                  {selectedPriceType === 'monthly' ? (
                    <Col md={12}>
                      <FormGroup className="mb-3">
                        <FormLabel>Monthly Price ($) *</FormLabel>
                        <Field
                          as={FormControl}
                          type="number"
                          step="0.01"
                          min="0.01"
                          name="priceMonthly"
                          placeholder="9.99"
                          isInvalid={errors.priceMonthly && touched.priceMonthly}
                        />
                        <ErrorMessage name="priceMonthly" component="div" className="text-danger small mt-1" />
                      </FormGroup>
                    </Col>
                  ) : (
                    <Col md={12}>
                      <FormGroup className="mb-3">
                        <FormLabel>Yearly Price ($) *</FormLabel>
                        <Field
                          as={FormControl}
                          type="number"
                          step="0.01"
                          min="0.01"
                          name="priceYearly"
                          placeholder="99.99"
                          isInvalid={errors.priceYearly && touched.priceYearly}
                        />
                        <ErrorMessage name="priceYearly" component="div" className="text-danger small mt-1" />
                      </FormGroup>
                    </Col>
                  )}
                </Row>

                <FormGroup className="mb-3">
                  <FormLabel>Description *</FormLabel>
                  <Field name="description">
                    {({ field }) => (
                      <FormControl
                        {...field}
                        as="textarea"
                        rows={3}
                        placeholder="Enter Description"
                        isInvalid={errors.description && touched.description}
                      />
                    )}
                  </Field>
                  <ErrorMessage name="description" component="div" className="text-danger small mt-1" />
                </FormGroup>

                <div className="mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <FormLabel>Plan Details *</FormLabel>
                    <Button
                      type="button"
                      variant="primary"
                      size="sm"
                      onClick={() => {
                        // Check if there are any empty features before adding
                        const hasEmptyFeatures = values.features.some(feature => !feature || feature.trim().length === 0);
                        if (hasEmptyFeatures) {
                          toast.warning('Please fill in all existing features before adding a new one');
                          return;
                        }
                        setFieldValue('features', [...values.features, '']);
                      }}
                      disabled={values.features.length >= 20}
                      style={{ background: "#3957A0", borderColor: "#3957A0" }}
                    >
                      Add
                    </Button>
                  </div>

                  <FieldArray name="features">
                    {({ remove }) => (
                      <div>
                        {values.features.map((feature, index) => (
                          <div key={index} className="d-flex align-items-start mb-2">
                            <div className="flex-grow-1 me-2">
                              <Field
                                as={FormControl}
                                name={`features.${index}`}
                                placeholder="Priority Gift Processing"
                                isInvalid={errors.features && errors.features[index] && touched.features && touched.features[index]}
                              />
                              {errors.features && errors.features[index] && touched.features && touched.features[index] && (
                                <div className="text-danger small mt-1">{errors.features[index]}</div>
                              )}
                            </div>
                            <Button
                              type="button"
                              variant="outline-danger"
                              size="sm"
                              onClick={() => remove(index)}
                              disabled={values.features.length <= 1}
                              style={{
                                minWidth: '32px',
                                minHeight: '32px',
                                padding: '6px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                            >
                              <span style={{ fontSize: '14px', fontWeight: 'bold' }}>×</span>
                            </Button>
                          </div>
                        ))}
                        {errors.features && typeof errors.features === 'string' && (
                          <div className="text-danger small mt-1">{errors.features}</div>
                        )}
                      </div>
                    )}
                  </FieldArray>
                </div>

                {/* <div className="mb-3">
                  <div className="d-flex align-items-center">
                    <span className="me-2">Enable</span>
                    <Field name="isEnabled">
                      {({ field }) => (
                        <Form.Check
                          type="switch"
                          id="enable-switch"
                          {...field}
                          checked={field.value}
                          onChange={(e) => setFieldValue('isEnabled', e.target.checked)}
                        />
                      )}
                    </Field>
                  </div>
                </div> */}
              </Modal.Body>
              <Modal.Footer>
                <Button
                  variant="outline-secondary"
                  onClick={handleCloseModal}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  type="submit"
                  disabled={!isValid || isSubmitting || !hasChanges}
                  style={{ background: "#3957A0", borderColor: "#3957A0" }}
                >
                  {isSubmitting ? 'Updating...' : 'Update'}
                </Button>
              </Modal.Footer>
            </FormikForm>
            );
          }}
        </Formik>
      </Modal>

      {/* Export Modal */}
      <ExportModal
        show={showExportModal}
        onHide={() => setShowExportModal(false)}
        onExportExcel={handleExportExcel}
        onExportPdf={handleExportPdf}
        title="Export Subscription Plans Data"
      />
    </section>
  );
};

export default SubscriptionPlans;